import { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, Button, useTheme } from "@mui/material";

import axios from "axios";
import SimpleTable from "../../components/SimpleTable";
import { tokens } from "../../theme";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const ExpenseReport = () => {
  const theme = useTheme();
  // Get today's date in YYYY-MM-DD format
  const today = dayjs().format("YYYY-MM-DD");
  const colors = tokens(theme.palette.mode);

  const [expenses, setExpenses] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [count, setCount] = useState(0);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/expense/report`,
        { startDate, endDate },
      );
      setExpenses(res.data.expenses || []);
      setTotalExpenses(res.data.totalExpenses || 0);
      setCount(res.data.count || 0);
    } catch (err) {
      console.error(err);
      setExpenses([]);
      setTotalExpenses(0);
      setCount(0);
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const columns = [
    // { field: "id", headerName: "ID", width: 70 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "description", headerName: "Description", width: 300 },
    { field: "amount", headerName: "Amount ($)", width: 120 },
    {
      field: "expenseDate",
      headerName: "Expense Date",
      width: 150,
      valueFormatter: ({ value }) => dayjs.utc(value).format("YYYY-MM-DD"),
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <Box p={3}>
      <Typography variant="h4" mb={2}>
        Expense Report
      </Typography>

      {/* Date Filter */}
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </Box>

      {/* Summary */}
      <Box mb={2}>
        <Typography>Total Expenses: {totalExpenses}</Typography>
        {/* <Typography>Number of Records: {count}</Typography> */}
      </Box>

      {/* Table */}
      <SimpleTable
        rows={expenses}
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        pageSizeOptions={[5, 10, 25]}
        height="60vh"
        sx={gridSx}
      />
    </Box>
  );
};

export default ExpenseReport;
